import { equalYearMonthInfo } from '@/appLogic/interfaces/date';
import { IOrderBasisNextDailyData, IOrderBasisNextMonthlyData, IOrderBasisPastMonthlyData } from '@/coreDomainsModule/entities/orderBasis';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { MonthlyDataCalculationService } from '@/coreDomainsModule/services/monthlyDataCalculationService';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { ISalesResult } from '@/coreDomainsModule/entities/salesResults';
import { IArrival, OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';

/**
 * 発注根拠に関する共通サービス
 */
export class OrderBasisService {
  /**
   * 発注作成時に必要な6ヶ月分の発注根拠以後月次情報を生成する
   * @param customerAnnounce - オリジナルの客先内示情報
   * @param userAdjustedCustomerForecast - ユーザーが編集した客先内示情報
   * @param recommend - すでに確定済みの発注情報
   * @param businessForPreInventory - 予在庫計算用の集計IBusiness型データ
   * @param confirmedOrders - 今回登録する発注情報
   */
  public static getNextMonthlyInfosForCreateOrder(
    customerAnnounce: IAnnounce[],
    userAdjustedCustomerForecast: IAnnounce[],
    recommend: IArrival[],
    businessForPreInventory: IBusiness,
    confirmedOrders: IArrival[]
  ): IOrderBasisNextMonthlyData[] {
    const expandedRecommend = recommend
      .flatMap((r) => (Array.isArray(r.partialDeliveries) && r.partialDeliveries.length > 0 ? r.partialDeliveries : r))
      .concat(confirmedOrders);
    return Array.from({ length: 6 }, (_, i) => i++).reduce((infos: IOrderBasisNextMonthlyData[], monthDiff) => {
      // 現在の年月から+0, +1, +2, ... とした年月が対象年月
      const targetYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(monthDiff);
      // 対象年月が合っているもので絞って、その中で受領年月が最新のものを使用する
      const targetCustomerForecastList = customerAnnounce.filter((ca) => equalYearMonthInfo(ca, targetYearMonth));
      const latestTargetCustomerForecast =
        targetCustomerForecastList.length > 0
          ? targetCustomerForecastList.reduce((max, cur) =>
              cur.manageInitiatedYearMonth &&
              max.manageInitiatedYearMonth &&
              DateService.compareYearMonths(cur.manageInitiatedYearMonth, max.manageInitiatedYearMonth) === 1
                ? cur
                : max
            )
          : undefined;
      // 対象年月が合っているもので絞って、その中で受領年月が最新のものを使用する
      const targetAdjustedCustomerForecastList = userAdjustedCustomerForecast.filter((ca) => equalYearMonthInfo(ca, targetYearMonth));
      const targetAdjustedLatestCustomerForecast =
        targetAdjustedCustomerForecastList && targetAdjustedCustomerForecastList.length > 0
          ? targetAdjustedCustomerForecastList.reduce((max, cur) =>
              cur.manageInitiatedYearMonth &&
              max.manageInitiatedYearMonth &&
              DateService.compareYearMonths(cur.manageInitiatedYearMonth, max.manageInitiatedYearMonth) === 1
                ? cur
                : max
            )
          : undefined;
      // 在庫管理で表示している情報と同じものを保存するので、同じメソッドで取得
      const targetPreInventory = MonthlyDataCalculationService.calcExpectedEndInventory(
        { ...businessForPreInventory, recommend: [...businessForPreInventory.recommend, ...confirmedOrders] },
        monthDiff
      );
      // 日次情報作成
      const nextDailyInfoMap = expandedRecommend
        .filter(
          (a) =>
            a.crossDate &&
            equalYearMonthInfo(a.crossDate, targetYearMonth) &&
            a.status !== undefined &&
            a.status !== OrderStatus.Recommended &&
            a.status !== OrderStatus.ApprovalRequested &&
            a.status !== OrderStatus.DisplayEnded &&
            a.status !== OrderStatus.Unknown
        )
        .reduce((map, a) => {
          if (!a.crossDate) return map;
          const day = a.crossDate.day;
          map[day] = (map[day] ?? 0) + a.amount;
          return map;
        }, {} as Record<number, number>);

      if (
        latestTargetCustomerForecast !== undefined &&
        targetAdjustedLatestCustomerForecast !== undefined &&
        targetPreInventory !== undefined
      ) {
        const res: IOrderBasisNextMonthlyData = {
          orderBasisTargetYearMonth: targetYearMonth,
          snapshotCustomerForecastAmount: latestTargetCustomerForecast.amount,
          userAdjustedCustomerForecastAmount: targetAdjustedLatestCustomerForecast.amount,
          snapshotPreInventoryAmount: targetPreInventory,
          dailyInfos: Object.entries(nextDailyInfoMap).map(
            ([day, amount]): IOrderBasisNextDailyData => ({ orderBasisTargetDay: Number(day), snapshotArrivalAmount: amount })
          ),
        };
        return [...infos, res];
      }
      return infos;
    }, []);
  }
  /**
   * 発注作成時に必要な12ヶ月分の発注根拠過去月次情報を生成する
   * @param customerForecasts - 過去客先内示データ
   * @param salesResults - 過去販売実績データ
   */
  public static getPastMonthlyInfosForCreateOrder(
    customerForecasts: IAnnounce[],
    salesResults: ISalesResult[]
  ): IOrderBasisPastMonthlyData[] {
    return Array.from({ length: 12 }, (_, i) => ++i).reduce((infos: IOrderBasisPastMonthlyData[], monthDiff) => {
      // 現在の日付から-1, -2, -3, ... とした年月が対象年月
      const targetYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(-monthDiff);
      const targetCustomerForecastList = customerForecasts.filter((ca) => equalYearMonthInfo(ca, targetYearMonth));
      // 対象年月が合っているもので絞って、その中で受領年月が最新のものを使用する
      const targetLatestCustomerForecast =
        targetCustomerForecastList.length > 0
          ? targetCustomerForecastList.reduce((max, cur) =>
              cur.manageInitiatedYearMonth &&
              max.manageInitiatedYearMonth &&
              DateService.compareYearMonths(cur.manageInitiatedYearMonth, max.manageInitiatedYearMonth) === 1
                ? cur
                : max
            )
          : undefined;
      const targetSalesResult = salesResults.find((sr) => equalYearMonthInfo(sr, targetYearMonth));
      if (targetLatestCustomerForecast !== undefined && targetSalesResult !== undefined) {
        const res: IOrderBasisPastMonthlyData = {
          orderBasisTargetYearMonth: targetYearMonth,
          snapshotCustomerForecastAmount: targetLatestCustomerForecast.amount,
          snapshotSalesResultAmount: targetSalesResult.amount,
        };
        return [...infos, res];
      }
      return infos;
    }, []);
  }
}
